import React from 'react';
import { useGameStore } from '../store/gameStore';
import { SCENARIOS } from '../data/sectors';
import { Shield, Radio, Users, Target, CheckCircle } from 'lucide-react';

// First tutorial scenario; fallback to first available scenario
const TRAINING_SCENARIO_ID =
  SCENARIOS.find(s => s.id === 'tutorial-opsec')?.id ||
  SCENARIOS.find(s => s.id.startsWith('tutorial-'))?.id ||
  SCENARIOS[0].id;

// First main (non-tutorial) scenario for skip path
const MAIN_SCENARIO_ID =
  SCENARIOS.find(s => !s.id.startsWith('tutorial-'))?.id || SCENARIOS[0].id;

export const WelcomeModal: React.FC = () => {
  const { hasSeenTutorial, startGame } = useGameStore();

  if (hasSeenTutorial) return null;

  const handleStart = (tutorial: boolean) => {
    // tutorial=true → walk through steps, tutorial=false → skip overlay
    startGame(tutorial ? TRAINING_SCENARIO_ID : MAIN_SCENARIO_ID, tutorial);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md">
      <div className="bg-slate-900 border-2 border-red-900/50 p-1 rounded-sm max-w-3xl w-full shadow-2xl relative overflow-hidden">
        {/* Scanlines effect */}
        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-0 bg-[length:100%_2px,3px_100%]" />
        
        <div className="relative z-10 bg-slate-900/90 p-8 border border-red-900/30 h-full">
          <div className="flex justify-between items-start mb-8 border-b border-red-900/30 pb-4">
            <div>
              <h1 className="text-3xl font-bold text-red-500 tracking-widest font-mono">
                CLASSIFIED BRIEFING
              </h1>
              <p className="text-red-500/50 text-xs font-mono mt-1">EYES ONLY // CLEARANCE LEVEL 5</p>
            </div>
            <Shield className="text-red-500/20 w-12 h-12" />
          </div>

          <div className="space-y-6 text-slate-300 mb-8 font-mono">
            <p className="text-lg text-center text-red-400 font-semibold border border-red-900/30 py-2 bg-red-950/10">
              MISSION: SECURE THE INFORMATION ENVIRONMENT
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 p-4 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-2 text-slate-200 font-bold">
                  <Shield className="w-5 h-5 text-blue-500" />
                  <span>TRUST IS CURRENCY</span>
                </div>
                <p className="text-sm text-slate-400">
                  You don't capture territory with guns. You earn it with <strong>Trust</strong>. 
                  High trust (8.5+) secures a sector. Low trust makes it vulnerable.
                </p>
              </div>

              <div className="bg-slate-800/50 p-4 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-2 text-slate-200 font-bold">
                  <Target className="w-5 h-5 text-green-500" />
                  <span>MISSION OBJECTIVE</span>
                </div>
                <p className="text-sm text-slate-400">
                  Each level has a specific <strong>Target Sector</strong>. 
                  Raise its Trust Score to <strong>8.5 or higher</strong> to unlock the next mission.
                </p>
              </div>

              <div className="bg-slate-800/50 p-4 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-2 text-slate-200 font-bold">
                  <Radio className="w-5 h-5 text-yellow-500" />
                  <span>ACTIONS MATTER</span>
                </div>
                <p className="text-sm text-slate-400">
                  Use <strong>Listen</strong> to gather intel, <strong>Broadcast</strong> to inform, 
                  and <strong>Secure</strong> to protect. Every action costs AP.
                </p>
              </div>

              <div className="bg-slate-800/50 p-4 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-2 text-slate-200 font-bold">
                  <Users className="w-5 h-5 text-purple-500" />
                  <span>WIN HEARTS & MINDS</span>
                </div>
                <p className="text-sm text-slate-400">
                  Success depends on understanding the population. 
                  Read the sector descriptions and choose the right tool.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => handleStart(false)}
              className="px-6 py-3 rounded border border-slate-600 hover:bg-slate-800 text-slate-400 font-mono text-sm transition-colors"
            >
              SKIP TUTORIAL
            </button>
            <button
              onClick={() => handleStart(true)}
              className="px-8 py-3 rounded bg-red-900/20 border border-red-500/50 hover:bg-red-900/40 text-red-400 font-bold font-mono tracking-wider transition-all flex items-center gap-2 shadow-[0_0_15px_rgba(239,68,68,0.2)]"
            >
              <CheckCircle className="w-5 h-5" />
              START TRAINING
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
